/**
 * Module-grid construction (ISO/IEC 18004 §6.3-6.8): function patterns
 * (finders, separators, timing, alignment, dark module), the format- and
 * version-information reserved areas and their BCH-coded content, the
 * zigzag data-bit placement, and mask selection by lowest penalty score
 * (§6.8.3-6.8.4, "ANNEX C").
 */
import { bchDivide } from './galois-field'
import { ALIGNMENT_PATTERN_POSITIONS } from './tables'
import type { QrErrorCorrectionLevel } from './types'

export function matrixSize(version: number): number {
	return (version - 1) * 4 + 21
}

type Grid<T> = T[][]

function createGrid<T>(size: number, fill: T): Grid<T> {
	return Array.from({ length: size }, () => Array.from({ length: size }, () => fill))
}

function setModule(
	grid: Grid<boolean | null>,
	functionMask: Grid<boolean>,
	row: number,
	col: number,
	dark: boolean,
): void {
	grid[row][col] = dark
	functionMask[row][col] = true
}

/** A finder pattern's outer 7x7 dark/light rings + solid 3x3 center, plus its 1-module separator ring (§6.3.3). */
function placeFinderPattern(
	grid: Grid<boolean | null>,
	functionMask: Grid<boolean>,
	anchorRow: number,
	anchorCol: number,
): void {
	const size = grid.length
	for (let r = -1; r <= 7; r++) {
		for (let c = -1; c <= 7; c++) {
			const row = anchorRow + r
			const col = anchorCol + c
			if (row < 0 || col < 0 || row >= size || col >= size) continue
			const isSeparator = r === -1 || r === 7 || c === -1 || c === 7
			const onOuterRing = r >= 0 && r <= 6 && c >= 0 && c <= 6 && (r === 0 || r === 6 || c === 0 || c === 6)
			const inCenter = r >= 2 && r <= 4 && c >= 2 && c <= 4
			setModule(grid, functionMask, row, col, !isSeparator && (onOuterRing || inCenter))
		}
	}
}

/** An alignment pattern's 5x5 dark ring, 3x3 light ring, and solid center module (§6.3.6). */
function placeAlignmentPattern(
	grid: Grid<boolean | null>,
	functionMask: Grid<boolean>,
	centerRow: number,
	centerCol: number,
): void {
	for (let r = -2; r <= 2; r++) {
		for (let c = -2; c <= 2; c++) {
			const dark = r === -2 || r === 2 || c === -2 || c === 2 || (r === 0 && c === 0)
			setModule(grid, functionMask, centerRow + r, centerCol + c, dark)
		}
	}
}

function overlapsInterval(aMin: number, aMax: number, bMin: number, bMax: number): boolean {
	return aMin <= bMax && bMin <= aMax
}

/**
 * Every function pattern except the format-/version-information content
 * itself (that's `applyFormatInfo`/`placeVersionInfo`, layered on after
 * masking picks a winner): 3 finders + separators, the horizontal/vertical
 * timing patterns, every alignment pattern whose 5x5 box doesn't overlap a
 * finder (an alignment center legitimately sitting ON the timing-pattern
 * line — e.g. row 6 — is NOT a finder overlap and must still be placed),
 * the dark module, and the reserved (blanked) format-info strips.
 */
function placeFunctionPatterns(grid: Grid<boolean | null>, functionMask: Grid<boolean>, version: number): void {
	const size = grid.length
	placeFinderPattern(grid, functionMask, 0, 0)
	placeFinderPattern(grid, functionMask, 0, size - 7)
	placeFinderPattern(grid, functionMask, size - 7, 0)

	for (let i = 8; i < size - 8; i++) {
		if (!functionMask[6][i]) setModule(grid, functionMask, 6, i, i % 2 === 0)
		if (!functionMask[i][6]) setModule(grid, functionMask, i, 6, i % 2 === 0)
	}

	const positions = ALIGNMENT_PATTERN_POSITIONS[version] ?? []
	const finderBoxes: readonly [number, number, number, number][] = [
		[0, 7, 0, 7],
		[0, 7, size - 8, size - 1],
		[size - 8, size - 1, 0, 7],
	]
	for (const row of positions) {
		for (const col of positions) {
			const overlapsFinder = finderBoxes.some(
				([r0, r1, c0, c1]) => overlapsInterval(row - 2, row + 2, r0, r1) && overlapsInterval(col - 2, col + 2, c0, c1),
			)
			if (overlapsFinder) continue
			placeAlignmentPattern(grid, functionMask, row, col)
		}
	}

	// dark module (§6.3.8) — always at (8, 4*version + 9), placed before the
	// format-info strips below so their loop bounds can leave it untouched.
	setModule(grid, functionMask, 4 * version + 9, 8, true)

	// format-info strips (§6.9): top-left copy — row 8 cols 0-8 and col 8 rows
	// 0-8, minus the timing-pattern intersections already set — plus the
	// second copy: row 8's trailing 8 cells, and col 8's trailing 7 cells
	// (NOT 8 — the 8th would be the dark module set above, which this must
	// not touch).
	for (let i = 0; i < 9; i++) {
		if (!functionMask[8][i]) setModule(grid, functionMask, 8, i, false)
		if (!functionMask[i][8]) setModule(grid, functionMask, i, 8, false)
	}
	for (let i = 0; i < 8; i++) setModule(grid, functionMask, 8, size - 1 - i, false)
	for (let i = 0; i < 7; i++) setModule(grid, functionMask, size - 1 - i, 8, false)

	// version-info reserved areas (§6.10), versions 7+: a 6x3 block above the
	// bottom-left finder and its 3x6 mirror left of the top-right finder.
	if (version >= 7) {
		for (let r = 0; r < 6; r++) {
			for (let c = 0; c < 3; c++) {
				setModule(grid, functionMask, size - 11 + c, r, false)
				setModule(grid, functionMask, r, size - 11 + c, false)
			}
		}
	}
}

/**
 * The zigzag data-placement walk (§6.7.3): two-module-wide columns from the
 * bottom-right, alternating upward/downward, skipping the vertical timing
 * column (col 6) entirely and any already-occupied (function/reserved)
 * module within a column.
 */
function placeDataBits(grid: Grid<boolean | null>, functionMask: Grid<boolean>, bits: readonly number[]): void {
	const size = grid.length
	let bitIndex = 0
	let col = size - 1
	let goingUp = true
	while (col > 0) {
		if (col === 6) col--
		for (let i = 0; i < size; i++) {
			const row = goingUp ? size - 1 - i : i
			for (const c of [col, col - 1]) {
				if (functionMask[row][c]) continue
				const bit = bitIndex < bits.length ? bits[bitIndex] : 0
				grid[row][c] = bit === 1
				bitIndex++
			}
		}
		col -= 2
		goingUp = !goingUp
	}
}

/** The eight ISO/IEC 18004 §6.8.1 mask formulas — true means "invert this module". */
const MASK_FUNCTIONS: readonly ((row: number, col: number) => boolean)[] = [
	(row, col) => (row + col) % 2 === 0,
	row => row % 2 === 0,
	(_row, col) => col % 3 === 0,
	(row, col) => (row + col) % 3 === 0,
	(row, col) => (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0,
	(row, col) => ((row * col) % 2) + ((row * col) % 3) === 0,
	(row, col) => (((row * col) % 2) + ((row * col) % 3)) % 2 === 0,
	(row, col) => (((row + col) % 2) + ((row * col) % 3)) % 2 === 0,
]

function applyMask(grid: Grid<boolean | null>, functionMask: Grid<boolean>, maskIndex: number): Grid<boolean | null> {
	const size = grid.length
	const masked = grid.map(row => [...row])
	const shouldInvert = MASK_FUNCTIONS[maskIndex]
	for (let row = 0; row < size; row++) {
		for (let col = 0; col < size; col++) {
			if (functionMask[row][col]) continue
			if (shouldInvert(row, col)) masked[row][col] = !masked[row][col]
		}
	}
	return masked
}

/** Penalty rule 1's per-line score: 3 points for a same-color run of 5, +1 for each module beyond it. */
function scoreRun(values: readonly (boolean | null)[]): number {
	let runScore = 0
	let runColor: boolean | null = null
	let runLength = 0
	for (const value of values) {
		if (value === runColor) {
			runLength++
		} else {
			if (runLength >= 5) runScore += 3 + (runLength - 5)
			runColor = value
			runLength = 1
		}
	}
	if (runLength >= 5) runScore += 3 + (runLength - 5)
	return runScore
}

// Penalty rule 3's finder-like 1:1:3:1:1 run (with the 4-light quiet run on
// one side) — dark-light-dark-dark-dark-light-dark, forward and reversed.
const FINDER_LIKE_PATTERN_A = [true, false, true, true, true, false, true, false, false, false, false]
const FINDER_LIKE_PATTERN_B = [false, false, false, false, true, false, true, true, true, false, true]

function matchesAt(values: readonly (boolean | null)[], start: number, pattern: readonly boolean[]): boolean {
	for (let i = 0; i < pattern.length; i++) if (values[start + i] !== pattern[i]) return false
	return true
}

/** ISO/IEC 18004 §6.8.3's four penalty rules — lower is better; the winning mask minimizes this. */
function penaltyScore(grid: Grid<boolean | null>): number {
	const size = grid.length
	let score = 0

	for (let row = 0; row < size; row++) score += scoreRun(grid[row])
	for (let col = 0; col < size; col++) score += scoreRun(grid.map(row => row[col]))

	for (let row = 0; row < size - 1; row++) {
		for (let col = 0; col < size - 1; col++) {
			const value = grid[row][col]
			if (value === grid[row][col + 1] && value === grid[row + 1][col] && value === grid[row + 1][col + 1]) score += 3
		}
	}

	for (let row = 0; row < size; row++) {
		for (let col = 0; col <= size - 11; col++) {
			if (matchesAt(grid[row], col, FINDER_LIKE_PATTERN_A) || matchesAt(grid[row], col, FINDER_LIKE_PATTERN_B))
				score += 40
		}
	}
	for (let col = 0; col < size; col++) {
		const column = grid.map(row => row[col])
		for (let row = 0; row <= size - 11; row++) {
			if (matchesAt(column, row, FINDER_LIKE_PATTERN_A) || matchesAt(column, row, FINDER_LIKE_PATTERN_B)) score += 40
		}
	}

	let darkCount = 0
	for (const row of grid) for (const value of row) if (value) darkCount++
	const darkPercent = (darkCount / (size * size)) * 100
	const previousMultipleOf5 = Math.floor(darkPercent / 5) * 5
	const nextMultipleOf5 = previousMultipleOf5 + 5
	score += Math.min(Math.abs(previousMultipleOf5 - 50), Math.abs(nextMultipleOf5 - 50)) * 2 // (/5)*10 == *2

	return score
}

const FORMAT_GENERATOR = 0b10100110111 // BCH(15,5) generator, ISO/IEC 18004 §6.9: x^10+x^8+x^5+x^4+x^2+x+1
const FORMAT_MASK = 0b101010000010010 // the fixed XOR mask §6.9 applies to every format string
const LEVEL_INDICATOR_BITS: Record<QrErrorCorrectionLevel, number> = { L: 0b01, M: 0b00, Q: 0b11, H: 0b10 }

/** The 15-bit format string (error-correction level + mask, BCH-coded and XOR-masked) for one candidate mask. */
function formatInfoBits(level: QrErrorCorrectionLevel, maskIndex: number): number {
	const data = (LEVEL_INDICATOR_BITS[level] << 3) | maskIndex // 5 bits
	const eccBits = bchDivide(data << 10, FORMAT_GENERATOR, 11) // 10-bit remainder
	return ((data << 10) | eccBits) ^ FORMAT_MASK
}

/** Places the 15-bit format string in both its copies (§6.9), bit 0 = most significant. */
function placeFormatInfo(grid: Grid<boolean | null>, bits: number): void {
	const size = grid.length
	const bitAt = (i: number): boolean => ((bits >> (14 - i)) & 1) === 1
	for (let i = 0; i < 6; i++) grid[8][i] = bitAt(i)
	grid[8][7] = bitAt(6)
	grid[8][8] = bitAt(7)
	grid[7][8] = bitAt(8)
	for (let i = 9; i < 15; i++) grid[14 - i][8] = bitAt(i)

	for (let i = 0; i < 8; i++) grid[8][size - 1 - i] = bitAt(i)
	for (let i = 0; i < 7; i++) grid[size - 1 - i][8] = bitAt(14 - i)
}

const VERSION_GENERATOR = 0b1111100100101 // BGH(18,6) generator, ISO/IEC 18004 §6.10: x^12+x^11+x^10+x^9+x^8+x^5+x^2+1

/** The 18-bit version-information string (6-bit version, BCH-coded), versions 7+. */
function versionInfoBits(version: number): number {
	const eccBits = bchDivide(version << 12, VERSION_GENERATOR, 13) // 12-bit remainder
	return (version << 12) | eccBits
}

/** Places the 18-bit version string in its bottom-left (6x3) and top-right (3x6) blocks (§6.10). */
function placeVersionInfo(grid: Grid<boolean | null>, version: number): void {
	const size = grid.length
	const bits = versionInfoBits(version)
	const bitAt = (i: number): boolean => ((bits >> i) & 1) === 1 // bit 0 = least significant, per spec's own indexing
	for (let col = 0; col < 6; col++) {
		for (let row = 0; row < 3; row++) {
			grid[size - 11 + row][col] = bitAt(col * 3 + row)
		}
	}
	for (let row = 0; row < 6; row++) {
		for (let col = 0; col < 3; col++) {
			grid[row][size - 11 + col] = bitAt(row * 3 + col)
		}
	}
}

/**
 * Places `bits` (the interleaved data+ECC codewords plus remainder bits
 * from `bit-stream.ts`) into a fresh `version`-sized matrix: function
 * patterns and reserved areas first, then data, then the lowest-penalty
 * mask (trying all 8 candidates, per §6.8.4) with its format/version info.
 */
export function buildModuleMatrix(
	bits: readonly number[],
	version: number,
	level: QrErrorCorrectionLevel,
): boolean[][] {
	const size = matrixSize(version)
	const grid: Grid<boolean | null> = createGrid(size, null)
	const functionMask: Grid<boolean> = createGrid(size, false)

	placeFunctionPatterns(grid, functionMask, version)
	if (version >= 7) placeVersionInfo(grid, version)
	placeDataBits(grid, functionMask, bits)

	// Seed with mask 0 so `bestGrid` never needs a null/undefined branch —
	// masks 1-7 only replace it when they score strictly lower.
	let bestGrid = applyMask(grid, functionMask, 0)
	placeFormatInfo(bestGrid, formatInfoBits(level, 0))
	let bestScore = penaltyScore(bestGrid)
	for (let maskIndex = 1; maskIndex < 8; maskIndex++) {
		const masked = applyMask(grid, functionMask, maskIndex)
		placeFormatInfo(masked, formatInfoBits(level, maskIndex))
		const score = penaltyScore(masked)
		if (score < bestScore) {
			bestScore = score
			bestGrid = masked
		}
	}

	return bestGrid.map(row => row.map(value => value === true))
}
